/**
 * ShareBar.tsx
 * A polished action strip that gives users two export options:
 *   1. Copy shareable URL to clipboard — encodes all slider state into ?s= param
 *   2. Download branded PDF cost report — client-side, no server needed
 *
 * Designed to sit between the CalculatorPanel and the dashboard grid in Home.tsx,
 * matching the Cosmic Slate dark theme.
 */

import { useState, useCallback } from 'react'
import {Link2, FileDown, Check, Loader2} from 'lucide-react'
import type { CalcInputs, ProviderResult } from '@/lib/pricing'
import { buildShareUrl, pushInputsToUrl } from '@/lib/shareUrl'

interface Props {
  inputs: CalcInputs
  results: ProviderResult[]
}

type CopyState = 'idle' | 'copied' | 'error'
type PdfState  = 'idle' | 'generating' | 'done' | 'error'

function ShareBar({ inputs, results }: Props) {
  const [copyState, setCopyState] = useState<CopyState>('idle')
  const [pdfState,  setPdfState]  = useState<PdfState>('idle')

  // ── Copy shareable URL ───────────────────────────────────────────────────
  const handleCopyUrl = useCallback(async () => {
    if (copyState !== 'idle') return
    try {
      pushInputsToUrl(inputs)                     // keep address bar in sync
      const url = buildShareUrl(inputs)
      await navigator.clipboard.writeText(url)
      setCopyState('copied')
      setTimeout(() => setCopyState('idle'), 2200)
    } catch {
      setCopyState('error')
      setTimeout(() => setCopyState('idle'), 2200)
    }
  }, [inputs, copyState])

  // ── Download PDF ─────────────────────────────────────────────────────────
  const handlePdf = useCallback(async () => {
    if (pdfState !== 'idle') return
    setPdfState('generating')
    try {
      // Lazy-load pdfReport to keep main bundle slim
      const { generatePdfReport } = await import('@/lib/pdfReport')
      await generatePdfReport(inputs, results)
      setPdfState('done')
      setTimeout(() => setPdfState('idle'), 2500)
    } catch (err) {
      console.error('[ShareBar] PDF generation failed:', err)
      setPdfState('error')
      setTimeout(() => setPdfState('idle'), 2500)
    }
  }, [inputs, results, pdfState])

  // ── render helpers ───────────────────────────────────────────────────────
  const copyLabel = copyState === 'copied' ? 'Copied!' : copyState === 'error' ? 'Failed' : 'Share URL'
  const pdfLabel  = pdfState === 'generating' ? 'Generating…' : pdfState === 'done' ? 'Downloaded!' : pdfState === 'error' ? 'Failed' : 'PDF Report'

  return (
    <div className="flex flex-wrap items-center gap-2.5 py-3 px-4 rounded-xl border border-[#1F2A44] bg-[#0B0F1C]/60 backdrop-blur-sm">
      {/* Left label */}
      <div className="flex items-center gap-2 mr-auto min-w-0">
        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400/60 shrink-0" />
        <span className="text-[11px] font-mono uppercase tracking-[0.16em] text-slate-500 truncate">
          Export scenario
        </span>
      </div>

      {/* ── Copy URL button ───────────────────────────────────────────────── */}
      <button
        onClick={handleCopyUrl}
        disabled={copyState !== 'idle'}
        aria-label="Copy shareable URL for current configuration"
        className={[
          'group inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-[12px] font-semibold',
          'transition-all duration-150 select-none shrink-0',
          copyState === 'copied'
            ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300 cursor-default'
            : copyState === 'error'
            ? 'border-rose-500/50 bg-rose-500/10 text-rose-300 cursor-default'
            : 'border-[#1F2A44] bg-[#0E1525] text-slate-300 hover:border-cyan-500/40 hover:bg-cyan-500/5 hover:text-cyan-200 active:scale-95',
        ].join(' ')}
      >
        {copyState === 'copied'
          ? <Check className="w-3.5 h-3.5 text-emerald-400" strokeWidth={2.5} />
          : <Link2 className="w-3.5 h-3.5" strokeWidth={2} />
        }
        {copyLabel}
      </button>

      {/* ── PDF download button ───────────────────────────────────────────── */}
      <button
        onClick={handlePdf}
        disabled={pdfState !== 'idle'}
        aria-label="Download PDF cost report for current configuration"
        className={[
          'group inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-[12px] font-semibold',
          'transition-all duration-150 select-none shrink-0',
          pdfState === 'done'
            ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300 cursor-default'
            : pdfState === 'error'
            ? 'border-rose-500/50 bg-rose-500/10 text-rose-300 cursor-default'
            : pdfState === 'generating'
            ? 'border-[#2A3656] bg-[#0E1525] text-slate-500 cursor-wait'
            : 'border-[#1F2A44] bg-gradient-to-r from-cyan-500/10 to-emerald-500/10 text-slate-200 hover:from-cyan-500/20 hover:to-emerald-500/20 hover:text-white hover:border-cyan-500/40 active:scale-95',
        ].join(' ')}
      >
        {pdfState === 'generating'
          ? <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
          : pdfState === 'done'
          ? <Check className="w-3.5 h-3.5 text-emerald-400" strokeWidth={2.5} />
          : <FileDown className="w-3.5 h-3.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={2} />
        }
        {pdfLabel}
      </button>

      {/* Supplementary info line */}
      <p className="w-full text-[10px] font-mono text-slate-600 leading-tight mt-0.5">
        Share URL encodes all slider values — recipients land on the exact same configuration.
        PDF report includes cost matrix, allocation chart, and workload parameters.
      </p>
    </div>
  )
}

export default ShareBar
